import './App.css';
import React, { useState, useRef } from 'react';
import HexGrid from './components/HexGrid';
import { evolveGameWithThresholds } from './util';
import ControlPanel from './components/ControlPanel';
import { GameSearch, GameSearchOptions } from './game-info';
import Sunburst from './components/Sunburst';

const RATIO = window.innerHeight / window.innerWidth;
const INITIAL_HIDE_CONTROLS = RATIO > 0.75;

const DEFAULT_GAME: GameSearch = {
  is: 'unstarted',
  variant: 'normal',
  imbalance: [0.25, 1.5],
  adjacencies: [0, 4],
  desertPlacement: 'anywhere',
  collisions: [0, 2],
};

export default function App() {

  const [existingSearch, setGame] = useState(DEFAULT_GAME);
  const [drawerClosed, setDrawerClosed] = useState(INITIAL_HIDE_CONTROLS);
  const searchRef = useRef(Symbol());

  async function findNewGame(opts: GameSearchOptions) {
    const searchId = Symbol();
    searchRef.current = searchId;
    for await (const thisSearch of evolveGameWithThresholds(opts)) {
      if (searchRef.current !== searchId) {
        // Bail, since a new search has started
        return;
      } else {
        setGame(thisSearch);
      }
    }
  }

  let content: JSX.Element;

  switch (existingSearch.is) {
    case 'unstarted': {
      content = (
        <p id="info" className="main-area">
          Greg's<br/>Catan&nbsp;Board<br/>Generator!
        </p>
      );
      break;
    }
    case 'done': {
      content = (
        <div id="hexes" className="main-area">
          <svg viewBox="0 0 1000 1000">
            <HexGrid
              sideLength={existingSearch.variant === 'normal' ? 90 : 70}
              x={500}
              y={500}
              hexes={existingSearch.hexes}
              variant={existingSearch.variant}
            />
          </svg>
        </div>
      );
      break;
    }
    case 'in-progress': {
      content = (
        <p id="trying" className="main-area">
          { existingSearch.generations.toFixed(0).padStart(6, '0') } generations
        </p>
      );
      break;
    }
    case 'paused': {
      content = (
        <div id="resume" className="main-area">
          <p>
            Either my search algorithm sucks, or the board you've
            specified mathematically cannot exist. Perhaps try
            widening your search parameters.
          </p>
          <div id="resume-buttons">
            <button
              onClick={() => {
                setGame({ ...existingSearch, hexes: existingSearch.imperfectHexes, is: 'done' });
              }}
              children="USE IMPERFECT"
            />
            <button
              onClick={() => {
                findNewGame(existingSearch);
              }}
              children="KEEP LOOKING"
            />
          </div>
        </div>
      );
      break;
    }
  }

  return (
    <div id="wrapper" className={drawerClosed ? 'closed' : 'open'}>
      {/* <svg id="circle" className="main-area" viewBox="0 0 1000 1000">
        <circle
          cx="500"
          cy="500"
          r="480"
          fill="#38c"
        />
      </svg> */}
      {SUNBURST}
      { content }
      <ControlPanel
        search={existingSearch}
        drawerClosed={drawerClosed}
        onSetDrawerClosed={setDrawerClosed}
        onUpdate={opts => {
          findNewGame(opts);
          setDrawerClosed(INITIAL_HIDE_CONTROLS);
        }}
      />
    </div>
  );
}

const SUNBURST = (
  <div id="sunburst-container" className="main-area">
    <Sunburst
      id="sunburst"
      rayCount={40}
      rayColorCenter="rgba(255,255,255,0.0)"
      rayColorOuter="rgba(255,255,255,0.2)"
      cx={0.5}
      cy={0.5}
    />
  </div>
);
